/**
 * Landing Page - Shared Components
 * =================================
 * Reusable components for landing page sections
 * UPDATED: Matches LandingPagePremium.jsx styling
 */

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// Animation variants
export const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.2, 0.65, 0.3, 0.9] }
  }
};

export const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 }
  }
};

// Custom hook for counting animation
export const useCounter = (end, duration = 2000) => {
  const [count, setCount] = React.useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  React.useEffect(() => {
    if (!inView) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, end, duration]);

  return { count, ref };
};

// Animated section wrapper
export const AnimatedSection = ({ children, className = '', delay = 0, ...rest }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, delay, ease: [0.2, 0.65, 0.3, 0.9] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.section>
  );
};

// Bento card with slide animations - Matches LandingPagePremium styling
export const BentoCard = ({ children, className = '', span = '', slideFrom = 'bottom' }) => {
  const slideVariants = {
    left: {
      hidden: { opacity: 0, x: -100 },
      visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } }
    },
    right: {
      hidden: { opacity: 0, x: 100 },
      visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } }
    },
    bottom: {
      hidden: { opacity: 0, y: 40 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.2, 0.65, 0.3, 0.9] } }
    },
    top: {
      hidden: { opacity: 0, y: -40 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.2, 0.65, 0.3, 0.9] } }
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      variants={slideVariants[slideFrom]}
      className={`
        relative overflow-hidden rounded-3xl bg-white 
        border border-slate-200/80 
        shadow-[0_4px_20px_rgb(0,0,0,0.04)]
        hover:shadow-[0_20px_50px_rgb(0,0,0,0.1)] 
        transition-all duration-500 hover:-translate-y-1
        ${span} ${className}
      `}
    >
      {children}
    </motion.div>
  );
};

// Feature icon with gradient background - Larger size matching landing page
export const FeatureIcon = ({ icon: Icon, gradient = 'from-slate-900 to-slate-700' }) => (
  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
    <Icon className="w-8 h-8 text-white" strokeWidth={1.5} />
  </div>
);

// Stat item with counter animation - Responsive sizing matching landing page
export const StatItem = ({ value, label, suffix = '' }) => {
  const { count, ref } = useCounter(parseInt(value), 2000);
  return (
    <div ref={ref} className="text-center">
      <div className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-900">
        {count}{suffix}
      </div>
      <div className="mt-1 text-[10px] sm:text-xs md:text-sm lg:text-base text-slate-500 font-medium">{label}</div>
    </div>
  );
};

// Floating 3D orb for decorative backgrounds
export const Floating3DOrb = ({ size = 80, delay = 0, position }) => (
  <motion.div
    className="absolute pointer-events-none"
    style={{
      width: size,
      height: size,
      ...position
    }}
    animate={{
      y: [0, -20, 0],
      rotate: [0, 180, 360],
    }}
    transition={{
      duration: 8,
      delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  >
    <div 
      className="w-full h-full rounded-full"
      style={{
        background: `
          radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8) 0%, transparent 50%),
          radial-gradient(circle at 70% 70%, rgba(0,0,0,0.1) 0%, transparent 50%),
          linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)
        `,
        boxShadow: '0 20px 40px rgba(0,0,0,0.1), inset 0 -10px 30px rgba(0,0,0,0.05)'
      }}
    />
  </motion.div>
);
